import React, { useState } from 'react';
import { Settings, Copy, Plus, Trash2, Download } from 'lucide-react';

const EnvGeneratorTool = () => {
  const [variables, setVariables] = useState([
    { key: '', value: '', comment: '' }
  ]);
  const [format, setFormat] = useState('dotenv'); // dotenv, json, yaml, docker
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const addVariable = () => {
    setVariables([...variables, { key: '', value: '', comment: '' }]);
  };

  const updateVariable = (index, field, value) => {
    const updated = [...variables];
    updated[index][field] = value;
    setVariables(updated);
  };

  const removeVariable = (index) => {
    setVariables(variables.filter((_, i) => i !== index));
  };

  const generateOutput = () => {
    const validVars = variables.filter(v => v.key.trim());
    
    if (validVars.length === 0) {
      setOutput('');
      return;
    }

    let result = '';

    switch (format) {
      case 'dotenv':
        result = validVars.map(v => {
          let line = '';
          if (v.comment) line += `# ${v.comment}\n`;
          const value = v.value.includes(' ') ? `"${v.value}"` : v.value;
          line += `${v.key}=${value}`;
          return line;
        }).join('\n');
        break;

      case 'json':
        const jsonObj = {};
        validVars.forEach(v => {
          jsonObj[v.key] = v.value;
        });
        result = JSON.stringify(jsonObj, null, 2);
        break;

      case 'yaml':
        result = validVars.map(v => {
          let line = '';
          if (v.comment) line += `# ${v.comment}\n`;
          const value = v.value.includes(':') || v.value.includes('#') ? `"${v.value}"` : v.value;
          line += `${v.key}: ${value}`;
          return line;
        }).join('\n');
        break;

      case 'docker':
        result = validVars.map(v => {
          let line = '';
          if (v.comment) line += `# ${v.comment}\n`;
          line += `ENV ${v.key}="${v.value}"`;
          return line;
        }).join('\n');
        break;

      case 'shell':
        result = validVars.map(v => {
          let line = '';
          if (v.comment) line += `# ${v.comment}\n`;
          line += `export ${v.key}="${v.value}"`;
          return line;
        }).join('\n');
        break;
    }

    setOutput(result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = () => {
    const extensions = { dotenv: '.env', json: '.json', yaml: '.yaml', docker: 'Dockerfile', shell: '.sh' };
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `environment${extensions[format]}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadTemplate = (template) => {
    const templates = {
      node: [
        { key: 'NODE_ENV', value: 'development', comment: 'Environment mode' },
        { key: 'PORT', value: '3000', comment: 'Server port' },
        { key: 'DATABASE_URL', value: 'mongodb://localhost:27017/mydb', comment: 'Database connection' },
        { key: 'JWT_SECRET', value: 'your-secret-key', comment: 'JWT secret for auth' },
        { key: 'API_KEY', value: '', comment: 'External API key' },
      ],
      react: [
        { key: 'REACT_APP_API_URL', value: 'http://localhost:3001/api', comment: 'Backend API URL' },
        { key: 'REACT_APP_NAME', value: 'My App', comment: 'Application name' },
        { key: 'REACT_APP_VERSION', value: '1.0.0', comment: 'App version' },
      ],
      python: [
        { key: 'FLASK_ENV', value: 'development', comment: 'Flask environment' },
        { key: 'SECRET_KEY', value: 'your-secret-key', comment: 'Application secret' },
        { key: 'DATABASE_URL', value: 'postgresql://localhost/mydb', comment: 'Database URL' },
        { key: 'REDIS_URL', value: 'redis://localhost:6379', comment: 'Redis connection' },
      ],
    };
    setVariables(templates[template] || [{ key: '', value: '', comment: '' }]);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2">
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            <option value="dotenv">.env (dotenv)</option>
            <option value="json">JSON</option>
            <option value="yaml">YAML</option>
            <option value="docker">Dockerfile</option>
            <option value="shell">Shell Export</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadTemplate('node')}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm rounded-lg text-gray-300"
          >
            Node.js
          </button>
          <button
            onClick={() => loadTemplate('react')}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm rounded-lg text-gray-300"
          >
            React
          </button>
          <button
            onClick={() => loadTemplate('python')}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-sm rounded-lg text-gray-300"
          >
            Python
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {variables.map((variable, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="text"
                value={variable.key}
                onChange={(e) => updateVariable(index, 'key', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                placeholder="VARIABLE_NAME"
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
              />
              <input
                type="text"
                value={variable.value}
                onChange={(e) => updateVariable(index, 'value', e.target.value)}
                placeholder="value"
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
              />
              <input
                type="text"
                value={variable.comment}
                onChange={(e) => updateVariable(index, 'comment', e.target.value)}
                placeholder="Comment (optional)"
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => removeVariable(index)}
              disabled={variables.length === 1}
              className="p-2 text-red-400 hover:bg-red-900/30 disabled:text-gray-600 rounded-lg transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button
          onClick={addVariable}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Plus size={16} />
          Add Variable
        </button>
        <button
          onClick={generateOutput}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Settings size={16} />
          Generate
        </button>
      </div>

      {output && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Output</label>
          <pre className="w-full bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm overflow-auto max-h-64">
            {output}
          </pre>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyToClipboard}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button
          onClick={downloadFile}
          disabled={!output}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Download size={16} />
          Download
        </button>
      </div>
    </div>
  );
};

export default EnvGeneratorTool;
